import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Minus, Plus, User } from "lucide-react";

// Types
export interface Guest {
  id: number;
  age: number;
}

interface GuestManagerProps {
  totalGuests: number;
  guests: Guest[];
  onAddGuest: () => void;
  onRemoveGuest: () => void;
  onUpdateGuestAge: (guestId: number, newAge: number) => void;
  disabled?: boolean;
}

const CounterButton = ({
  onClick,
  children,
  disabled = false
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    className="w-12 h-12 rounded-full bg-gray-50 border-gray-200"
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </Button>
);

export const GuestManager: React.FC<GuestManagerProps> = ({
  totalGuests,
  guests,
  onAddGuest,
  onRemoveGuest,
  onUpdateGuestAge,
  disabled = false
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <span>Total number of guests</span>
        <div className="flex items-center space-x-4">
          {!disabled && (
            <CounterButton onClick={onRemoveGuest} disabled={disabled}>
              <Minus className="w-5 h-5" />
            </CounterButton>
          )}
          <h4 className="w-8 text-center">
            {totalGuests}
          </h4>
          {!disabled && (
            <CounterButton onClick={onAddGuest} disabled={totalGuests >= 16}>
              <Plus className="w-5 h-5" />
            </CounterButton>
          )}
        </div>
      </div>

      {guests.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {guests.map((guest, index) => (
            <div
              key={guest.id}
              className="flex items-center gap-2 border border-gray-200 rounded-xl bg-gray-50 px-3 py-2"
            >
              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <Input
                type="number"
                min="0"
                max="120"
                value={guest.age}
                onChange={(e) => {
                  const newAge = parseInt(e.target.value) || 0;
                  onUpdateGuestAge(guest.id, Math.max(0, newAge));
                }}
                className="w-14 h-8 text-center rounded-lg px-1"
                disabled={disabled}
              />
              <span className="text-xs text-gray-400 whitespace-nowrap">y/o</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
